import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../i18n';

// Buy-package page, rebuilt on the ws5 `client` apigroup (2026-07-30, phase 2 of
// the package-booking spec). The route is unchanged — /external/buy-package/:lang?
// with ?userID=rec… — because campaign links already in inboxes point at it.
//
// THIS PAGE COMPUTES NO MONEY. Everything it used to hardcode (package sizes,
// cadence multipliers, add-on prices, gift services, promotion windows) now comes
// from GET /packages/offer, and every price shown comes from POST /packages/quote.
// Purchase is POST /packages/checkout followed by a redirect to the payment_url it
// returns; the browser never carries an amount, which is the defect the whole
// spec exists to close.
const API_BASE = 'https://api.wiseclean.ee/api:nnuLSRT_';
const SUPPORT_EMAIL = 'info@wiseclean.eu';

// PAGE-SIDE BUSINESS RULE, and the last one left here: the server validates only
// that `time` is HH:MM, so this list is the only thing keeping a package off a
// 03:00 slot. It belongs in /packages/offer next to the catalog; until it moves
// there, a slot already in the past is filtered out below rather than offered.
const TIME_SLOTS = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30', '18:00',
];

// The ws5 endpoints answer with English operator prose ("No completed cleaning
// found for this address", "Checkout is already in progress"). This page also
// ships in Estonian, so nothing off the wire is rendered verbatim: a recognised
// message maps onto an existing translated string and anything else falls back to
// the generic one. Keyed on the exact server string because that is the only
// discriminator a Xano precondition exposes — it answers {code, message} and
// cannot carry a machine-readable field.
const SERVER_MESSAGE_KEYS = {
    'Invalid userID': 'invalidLinkBody',
    'No completed cleaning found for this address': 'noAddressBody',
    'Cannot price a package for this address': 'noAddressBody',
    'Packages are not configured': 'loadFailedBody',
    'Payments are not configured': 'loadFailedBody',
    'A free additional service must be selected': 'selectGiftError',
    'first_date must be YYYY-MM-DD': 'selectFirstDateError',
    'time must be HH:MM': 'selectFirstDateError',
};

// Checkout failures whose contract is "this request_key is spent and NOTHING was
// created — come back with a fresh one". They are exactly the errors raised after
// the idempotency ledger row is written and before the Package row exists
// (packages_checkout_POST.xs: the two "Pricing failed" preconditions, the add-on
// reconciliation guard, and "Checkout could not be started").
//
// Every OTHER failure must REUSE the key. "Checkout is already in progress" means
// a concurrent twin is mid-create and the page must poll with the SAME key;
// "Payment initialisation failed" and "This package is already paid" happen after
// the Package row exists, and a fresh key there would buy a SECOND package for
// one intent. Without this list an unchanged selection reuses one key forever, so
// a single failed create locks the purchase permanently.
const CHECKOUT_KEY_SPENT_MESSAGES = [
    'Pricing failed',
    'Add-on selection does not match the priced quote',
    'Checkout could not be started',
];

// If assigning window.location.href does not actually navigate — an email client's
// in-app browser that blocks or defers a top-level navigation — the CTA must not
// stay dead at "Opening secure payment…" forever.
const REDIRECT_FALLBACK_MS = 6000;

// Re-quote is debounced so dragging a promo counter does not fire one request per
// click. Every in-flight quote is tagged with a sequence number and a late reply
// from a superseded selection is dropped.
const QUOTE_DEBOUNCE_MS = 350;

// Display captions for the fixed line keys the quote returns. The SET of lines,
// their order and every amount stay exactly as the server sent them; only these
// stable captions are localised, because the server labels them in English
// ("Cleaning", "Per cleaning"). Add-on and promotion lines are already localised
// server-side and fall through to `line.label` untouched.
const LINE_LABEL_KEYS = {
    base: 'quoteLineBase',
    cadence_discount: 'quoteLineCadenceDiscount',
    per_cleaning: 'quoteLinePerCleaning',
    cleanings: 'quoteLineCleanings',
    suburban: 'quoteLineSuburban',
};

// The cadence VALUES come from the catalog (`cadence_multipliers` keys) and are
// posted back verbatim; this map only supplies a translated caption for the three
// that exist today. An unknown cadence added in Airtable renders its raw key
// rather than disappearing from the dropdown.
const CADENCE_LABEL_KEYS = {
    'Weekly': 'frequencyWeekly',
    'Bi-weekly': 'frequencyBiweekly',
    'Tri-weekly': 'frequencyTriweekly',
};

function todayInTallinn() {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Tallinn',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value || '';
    return `${get('year')}-${get('month')}-${get('day')}`;
}

function nextDay(isoDate) {
    const d = new Date(`${isoDate}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
}

function serverMessageKey(message, fallbackKey) {
    return SERVER_MESSAGE_KEYS[String(message || '').trim()] || fallbackKey;
}

function formatMoney(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return n < 0 ? `-€${Math.abs(n).toFixed(2)}` : `€${n.toFixed(2)}`;
}

// Gift services are all priced €0, but their Airtable label is the paid-catalog
// name and still carries its retail tag ("Fridge cleaning (inside) +€9"). Printing
// that under "free additional service" reads as a charge, so the trailing price is
// dropped for display only. The rec-id posted to the server is never touched.
function giftLabel(name) {
    return String(name || '').replace(/\s*\+\s*€\s*\d+([.,]\d+)?\s*$/, '').trim();
}

// One key per attempt, reused while the submission is unchanged, so a double click
// or a retry after a network blip cannot create a second Package row (the server
// is idempotent on this value). Must be 8-64 characters.
function newRequestKey() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `pkg-${crypto.randomUUID()}`;
    }
    return `pkg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

async function readBody(response) {
    try {
        return await response.json();
    } catch (e) {
        return null;
    }
}

function BuyPackage() {
    const { lang } = useParams();
    const [searchParams] = useSearchParams();
    const userID = searchParams.get('userID');
    const { t, i18n } = useTranslation();

    const activeLang = ['en', 'et'].includes(lang) ? lang : 'en';

    const [step, setStep] = useState('loading');
    const [offer, setOffer] = useState(null);

    const [addressId, setAddressId] = useState('');
    const [packageCode, setPackageCode] = useState('');
    const [cadence, setCadence] = useState('');
    const [addonRecids, setAddonRecids] = useState([]);
    const [giftRecid, setGiftRecid] = useState('');
    const [promoUnits, setPromoUnits] = useState({});
    const [promoVariants, setPromoVariants] = useState({});
    const [firstDate, setFirstDate] = useState('');
    const [time, setTime] = useState('');
    const [comments, setComments] = useState('');
    const [method, setMethod] = useState('bank');

    const [quote, setQuote] = useState(null);
    const [quoteFor, setQuoteFor] = useState('');
    const [quoting, setQuoting] = useState(false);
    const [quoteError, setQuoteError] = useState(null);
    const quoteSeqRef = useRef(0);
    // Bumped to force a re-quote of an UNCHANGED selection. The quote effect keys
    // on the selection alone, so without this the price panel cannot be refreshed
    // after the server prices the same selection differently (see handleBuy).
    const [quoteNonce, setQuoteNonce] = useState(0);

    const [submitting, setSubmitting] = useState(false);
    const [checkoutError, setCheckoutError] = useState(null);
    const [formErrorKey, setFormErrorKey] = useState('');
    const [invalidField, setInvalidField] = useState('');
    const fieldRefs = useRef({});
    const requestKeyRef = useRef({ signature: '', key: '' });
    // Remembers the total the server priced when it disagreed with the total on
    // screen, so the second click on the SAME selection is an explicit
    // re-confirmation rather than a permanent block.
    const priceAckRef = useRef({ signature: '', summ: '' });
    const redirectTimerRef = useRef(null);
    // `submitting` alone cannot gate a double click: setState is async, so a
    // second click dispatched in the same tick still sees the old value and fires
    // a second checkout. The request key makes that harmless server-side (one key
    // = one Package row), but there is no reason to hit a payment-initialising
    // endpoint twice. This ref flips synchronously and is the real gate.
    const submittingRef = useRef(false);

    const [showHowItWorks, setShowHowItWorks] = useState(false);
    const modalRef = useRef(null);

    useEffect(() => {
        i18n.changeLanguage(activeLang);
    }, [activeLang, i18n]);

    // ── offer ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!userID) {
            setStep('invalid');
            return undefined;
        }
        let cancelled = false;
        setStep('loading');
        fetch(`${API_BASE}/packages/offer?userID=${encodeURIComponent(userID)}&lang=${encodeURIComponent(activeLang)}`)
            .then(async (r) => {
                const body = await readBody(r);
                if (!r.ok) throw new Error('offer');
                return body;
            })
            .then((body) => {
                if (cancelled) return;
                const addresses = Array.isArray(body?.addresses) ? body.addresses : [];
                const packages = Array.isArray(body?.packages) ? body.packages : [];
                const gifts = Array.isArray(body?.gift_options) ? body.gift_options : [];
                setOffer({
                    ...body,
                    addresses,
                    packages,
                    gift_options: gifts,
                    addons: Array.isArray(body?.addons) ? body.addons : [],
                    promotions: Array.isArray(body?.promotions) ? body.promotions : [],
                });
                // `source_ok` is the server's own "the Orders_completed read
                // succeeded" flag: packages_offer_GET degrades softly on an
                // Airtable failure and returns an EMPTY address list, which is
                // indistinguishable from a client who has never had a cleaning.
                // Branching on addresses.length alone tells every recipient of a
                // campaign send, during one Airtable blip, that they have no
                // cleaning history and should contact support — a confident,
                // wrong, dead-end message.
                if (body?.source_ok === false) {
                    setStep('error');
                    return;
                }
                if (addresses.length === 0) {
                    setStep('no-address');
                    return;
                }
                // The catalog and the gift list are both degrade-to-empty reads on
                // the server. Either one empty means checkout cannot succeed (a
                // gift is mandatory there), so fail here rather than after the
                // customer has filled the form.
                if (packages.length === 0 || gifts.length === 0) {
                    setStep('error');
                    return;
                }
                // Exactly one address is selected silently; with several the
                // customer picks. The old page always used an arbitrary one.
                if (addresses.length === 1) setAddressId(addresses[0].address_id);
                setStep('ready');
            })
            .catch(() => {
                if (!cancelled) setStep('error');
            });
        return () => {
            cancelled = true;
        };
    }, [userID, activeLang]);

    // A dialog that announces aria-modal must actually behave like one: Tab has to
    // stay inside it, the page behind it must not scroll, and focus has to go back
    // where it came from. Without the trap a keyboard or screen-reader user walks
    // straight out into the form while assistive tech still reports a modal.
    useEffect(() => {
        if (!showHowItWorks) return undefined;
        const previouslyFocused = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        modalRef.current?.focus();
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setShowHowItWorks(false);
                return;
            }
            if (e.key !== 'Tab') return;
            const root = modalRef.current;
            if (!root) return;
            const focusable = Array.from(root.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
            )).filter((el) => !el.disabled && el.offsetParent !== null);
            if (focusable.length === 0) {
                e.preventDefault();
                root.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;
            if (e.shiftKey && (active === first || active === root)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && (active === last || active === root)) {
                e.preventDefault();
                first.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
            if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
                previouslyFocused.focus();
            }
        };
    }, [showHowItWorks]);

    // A bfcache restore (the customer pressed Back on the payment page) brings the
    // JS heap back untouched, so `submitting` returns as true and the CTA stays
    // dead at "Opening secure payment…" with no way to retry.
    useEffect(() => {
        const onPageShow = (e) => {
            if (!e.persisted) return;
            submittingRef.current = false;
            setSubmitting(false);
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    useEffect(() => () => {
        if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    }, []);

    const addresses = offer?.addresses || [];
    const packages = offer?.packages || [];
    const giftOptions = offer?.gift_options || [];
    const addons = offer?.addons || [];
    const promotions = offer?.promotions || [];

    const selectedAddress = useMemo(
        () => addresses.find((a) => a.address_id === addressId) || null,
        [addresses, addressId],
    );
    const selectedPackage = useMemo(
        () => packages.find((p) => p.code === packageCode) || null,
        [packages, packageCode],
    );

    const cadenceEntries = useMemo(() => {
        const multipliers = selectedPackage?.cadence_multipliers;
        if (!multipliers || typeof multipliers !== 'object') return [];
        return Object.entries(multipliers).filter(([, m]) => Number(m) > 0);
    }, [selectedPackage]);

    // A cadence that the newly chosen package does not offer must not survive into
    // the quote: the server 400s on an unknown cadence.
    useEffect(() => {
        if (!cadence) return;
        if (cadenceEntries.length === 0) return;
        if (!cadenceEntries.some(([key]) => key === cadence)) setCadence('');
    }, [cadence, cadenceEntries]);

    const eligiblePromotions = useMemo(() => {
        if (!selectedPackage) return [];
        return promotions.filter((p) => {
            const applies = Array.isArray(p.applies_to_packages) ? p.applies_to_packages : [];
            return applies.length === 0 || applies.includes(selectedPackage.recid);
        });
    }, [promotions, selectedPackage]);

    // Only selections for promotions that are eligible for the CURRENT package are
    // sent, so a leftover choice from another package cannot reach the server.
    // State itself is kept, so switching back restores the selection.
    const promoSelections = useMemo(() => {
        const out = [];
        eligiblePromotions.forEach((p) => {
            if (p.kind === 'per_unit') {
                const units = Number(promoUnits[p.code] || 0);
                if (units > 0) out.push({ code: p.code, units });
            } else if (p.kind === 'variant') {
                const variant = promoVariants[p.code] || '';
                if (variant) out.push({ code: p.code, variant });
            }
        });
        return out;
    }, [eligiblePromotions, promoUnits, promoVariants]);

    // Identifies the selection a quote belongs to. `Buy` is gated on the stored
    // quote still matching this, so the breakdown on screen is always the one the
    // customer is buying — a quote from a selection they have since changed can
    // never be the last thing they saw before clicking.
    const quoteSignature = useMemo(() => JSON.stringify({
        addressId, packageCode, cadence, addonRecids, promoSelections, activeLang,
    }), [addressId, packageCode, cadence, addonRecids, promoSelections, activeLang]);

    // ── quote ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!userID || !addressId || !packageCode || !cadence) {
            quoteSeqRef.current += 1;
            setQuote(null);
            setQuoteFor('');
            setQuoteError(null);
            setQuoting(false);
            return undefined;
        }
        const seq = quoteSeqRef.current + 1;
        quoteSeqRef.current = seq;
        setQuoting(true);
        setQuoteError(null);
        const timer = setTimeout(() => {
            fetch(`${API_BASE}/packages/quote`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userID,
                    address_id: addressId,
                    package_code: packageCode,
                    cadence,
                    addon_recids: addonRecids,
                    promo_selections: promoSelections,
                    lang: activeLang,
                }),
            })
                .then(async (r) => {
                    const body = await readBody(r);
                    // A 400 carries a reason ("Unknown cadence", "No completed
                    // cleaning found for this address") — but in English operator
                    // prose, so it is mapped onto translated copy, never shown raw.
                    if (!r.ok) {
                        const err = new Error('quote');
                        err.serverMessage = body?.message || '';
                        throw err;
                    }
                    return body;
                })
                .then((body) => {
                    if (quoteSeqRef.current !== seq) return;
                    setQuote(body);
                    setQuoteFor(quoteSignature);
                    setQuoting(false);
                })
                .catch((e) => {
                    if (quoteSeqRef.current !== seq) return;
                    setQuote(null);
                    setQuoteFor('');
                    setQuoteError({ key: serverMessageKey(e.serverMessage, 'quoteFailed') });
                    setQuoting(false);
                });
        }, QUOTE_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [userID, addressId, packageCode, cadence, addonRecids, promoSelections, activeLang, quoteSignature, quoteNonce]);

    // A package cannot start today (owner rule, 2026-08-02): it is a recurring
    // series that has to be scheduled and staffed, so the earliest first cleaning
    // is tomorrow. This used to allow today whenever any slot was still ahead on
    // the clock, which also meant the visible slot list shrank through the day
    // and emptied entirely in the evening. With today excluded, every slot is
    // always offered and the "slot fell into the past while the form sat open"
    // case cannot arise.
    //
    // `min` on the input is a hint, not a guard: it is trivially bypassed and the
    // server only checks the YYYY-MM-DD shape. The submit handler re-checks.
    const minDate = nextDay(todayInTallinn());
    const availableSlots = TIME_SLOTS;

    const toggleAddon = useCallback((recid) => {
        setAddonRecids((current) => (current.includes(recid)
            ? current.filter((r) => r !== recid)
            : [...current, recid]));
    }, []);

    const setPromoUnitCount = useCallback((code, next) => {
        setPromoUnits((current) => ({ ...current, [code]: next }));
    }, []);

    const focusField = useCallback((name) => {
        const el = fieldRefs.current[name];
        if (!el) return;
        if (typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        if (typeof el.focus === 'function') el.focus({ preventScroll: true });
    }, []);

    const handleBuy = useCallback(() => {
        if (submittingRef.current) return;
        setCheckoutError(null);
        setFormErrorKey('');
        // Which field is empty, not just that one is: the error copy names none of
        // the nine controls, so the offending one is scrolled to and focused and
        // carries aria-invalid.
        // `firstDate < minDate` covers a date typed past the `min` hint and a form
        // left open overnight, where a once-valid tomorrow has become today.
        const missingField = (!addressId && 'address')
            || (!packageCode && 'package')
            || (!cadence && 'frequency')
            || ((!firstDate || firstDate < minDate) && 'firstDate')
            || (!time && 'time')
            || (!giftRecid && 'freeService')
            || '';
        if (missingField) {
            setFormErrorKey(missingField === 'freeService' ? 'selectGiftError' : 'selectFirstDateError');
            setInvalidField(missingField);
            focusField(missingField);
            return;
        }
        setInvalidField('');
        // Belt and braces behind the disabled button: never check out against a
        // quote that belongs to a different selection.
        if (!quote || quoting || quoteFor !== quoteSignature) return;

        const payload = {
            userID,
            address_id: addressId,
            package_code: packageCode,
            cadence,
            addon_recids: addonRecids,
            gift_option_recid: giftRecid,
            promo_selections: promoSelections,
            first_date: firstDate,
            time,
            comment: comments,
            method,
            locale: activeLang,
        };
        // The key identifies the PURCHASE, so it is scoped to the fields that say
        // which purchase this is. `comment`, `method` and `locale` are excluded on
        // purpose: they change the row's cosmetics or the payment page, not what is
        // being bought. Keying on the whole payload meant that a customer who hit
        // "Payment initialisation failed" — which is raised AFTER the Package row
        // is committed — and then edited the comment or flipped the bank/card radio
        // (both natural reactions) minted a new key and bought a SECOND package.
        // The server converges a same-key retry onto the existing row instead.
        const signature = JSON.stringify({
            userID,
            address_id: addressId,
            package_code: packageCode,
            cadence,
            addon_recids: addonRecids,
            gift_option_recid: giftRecid,
            promo_selections: promoSelections,
            first_date: firstDate,
            time,
        });
        if (requestKeyRef.current.signature !== signature || !requestKeyRef.current.key) {
            requestKeyRef.current = { signature, key: newRequestKey() };
        }

        submittingRef.current = true;
        setSubmitting(true);
        fetch(`${API_BASE}/packages/checkout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...payload, request_key: requestKeyRef.current.key }),
        })
            .then(async (r) => {
                const body = await readBody(r);
                if (!r.ok) {
                    const err = new Error('checkout');
                    err.serverMessage = body?.message || '';
                    throw err;
                }
                return body;
            })
            .then((body) => {
                if (!body?.payment_url) {
                    const err = new Error('checkout');
                    err.serverMessage = '';
                    throw err;
                }
                // The customer approved the number in the breakdown; checkout
                // re-quotes server-side and opens the payment for THAT result. The
                // two can differ — a promotion window that closed between the quote
                // and the click, an Options.Price edit while the page sat open on a
                // phone, or the quote's sorted/unsorted Orders_completed fallback
                // picking a different source row. Compare before leaving.
                // `summ` is what the package costs; `amount` is what is charged and
                // is legitimately €1 on the test account, so it is never the
                // comparand.
                const shownTotal = Number(quote?.total);
                const pricedTotal = Number(body.summ);
                const drifted = Number.isFinite(shownTotal)
                    && Number.isFinite(pricedTotal)
                    && shownTotal.toFixed(2) !== pricedTotal.toFixed(2);
                const alreadyAcked = priceAckRef.current.signature === quoteSignature
                    && priceAckRef.current.summ === pricedTotal.toFixed(2);
                if (drifted && !alreadyAcked) {
                    // Do not navigate. Re-quote so the panel shows the real price
                    // and let the customer confirm it with a second click. The
                    // request key is deliberately NOT rotated: the Package row was
                    // created and a fresh key would buy a second one — the retry
                    // converges onto the same row and opens a new link against it.
                    priceAckRef.current = { signature: quoteSignature, summ: pricedTotal.toFixed(2) };
                    submittingRef.current = false;
                    setSubmitting(false);
                    setCheckoutError({ key: 'checkoutFailed' });
                    setQuoteNonce((n) => n + 1);
                    return;
                }
                // Stay disabled while the browser leaves the page.
                window.location.href = body.payment_url;
                // …unless it does not leave. An in-app browser that blocks the
                // assignment would otherwise strand the CTA on "Opening secure
                // payment…" with the payment link nowhere in the DOM. A second
                // click reuses the same key, so it reopens a link against the same
                // Package row rather than buying twice.
                redirectTimerRef.current = setTimeout(() => {
                    submittingRef.current = false;
                    setSubmitting(false);
                }, REDIRECT_FALLBACK_MS);
            })
            .catch((e) => {
                submittingRef.current = false;
                // Two server failures need OPPOSITE retries and the page cannot
                // tell them apart from the HTTP status. A spent key must be
                // rotated or the customer is locked out of the purchase forever;
                // any other failure must reuse it or a second package is bought.
                if (CHECKOUT_KEY_SPENT_MESSAGES.includes(String(e.serverMessage || '').trim())) {
                    requestKeyRef.current = { signature: '', key: '' };
                }
                setCheckoutError({ key: serverMessageKey(e.serverMessage, 'checkoutFailed') });
                setSubmitting(false);
            });
    }, [
        addressId, packageCode, cadence, firstDate, time, giftRecid,
        quote, quoting, quoteFor, quoteSignature, focusField,
        userID, addonRecids, promoSelections, comments, method, activeLang,
    ]);

    // ── terminal screens ─────────────────────────────────────────────────────
    const shell = (children) => (
        <div style={styles.page}>
            <header style={styles.header}>
                <img src="/wiseclean-logo.png" alt="WiseClean" style={styles.logo} />
            </header>
            {children}
        </div>
    );

    if (step === 'error' || step === 'invalid' || step === 'no-address') {
        const title = step === 'no-address' ? t('noAddressTitle') : t('loadFailedTitle');
        let body = t('loadFailedBody');
        if (step === 'invalid') body = t('invalidLinkBody');
        if (step === 'no-address') body = t('noAddressBody');
        return shell(
            <main style={styles.errorMain}>
                <div style={styles.card}>
                    <h1 style={styles.errorTitle}>{title}</h1>
                    <p style={styles.errorBody}>{body}</p>
                    <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.supportLink}>
                        {t('contactSupport')} · {SUPPORT_EMAIL}
                    </a>
                </div>
            </main>,
        );
    }

    if (step === 'loading' || !offer) {
        return shell(
            <main style={styles.main}>
                <div style={styles.loadingText}>{t('loadingPackageDetails')}</div>
            </main>,
        );
    }

    // ── form ─────────────────────────────────────────────────────────────────
    const rooms = activeLang === 'et'
        ? (selectedAddress?.rooms_et || selectedAddress?.rooms)
        : selectedAddress?.rooms;
    const bathrooms = activeLang === 'et'
        ? (selectedAddress?.bathrooms_et || selectedAddress?.bathrooms)
        : selectedAddress?.bathrooms;

    const addressText = (a) => {
        const flat = a.flat === 0 || a.flat ? String(a.flat) : '';
        return flat ? `${a.street}, ${t('flatLabel')} ${flat}` : a.street;
    };

    const lineLabel = (line) => (LINE_LABEL_KEYS[line.key]
        ? t(LINE_LABEL_KEYS[line.key])
        : (line.label || ''));

    const packageNames = (recids) => (Array.isArray(recids) ? recids : [])
        .map((rid) => packages.find((p) => p.recid === rid)?.name)
        .filter(Boolean)
        .join(', ');

    const quoteLines = Array.isArray(quote?.lines)
        ? quote.lines.filter((l) => l && typeof l === 'object')
        : [];

    // Fresh quote only: a stored quote whose selection has since changed keeps the
    // button disabled until the re-quote lands.
    const quoteFresh = Boolean(quote) && quoteFor === quoteSignature;
    const canBuy = quoteFresh && !quoting && !submitting;

    return (
        <div style={styles.page}>
            <header style={styles.header} aria-hidden={showHowItWorks || undefined}>
                <img src="/wiseclean-logo.png" alt="WiseClean" style={styles.logo} />
            </header>
            <main style={styles.main} aria-hidden={showHowItWorks || undefined}>
                <div style={styles.card}>
                    <h1 style={styles.title}>{t('buyPackageTitle')}</h1>

                    {addresses.length > 1 ? (
                        <div style={styles.field}>
                            <label style={styles.label} htmlFor="address">
                                {t('chooseAddressLabel')}
                            </label>
                            <select
                                id="address"
                                ref={(el) => { fieldRefs.current.address = el; }}
                                aria-invalid={invalidField === 'address' || undefined}
                                style={styles.select}
                                value={addressId}
                                onChange={(e) => setAddressId(e.target.value)}
                                required
                            >
                                <option value="">{t('chooseAddressPlaceholder')}</option>
                                {addresses.map((a) => (
                                    <option key={a.address_id} value={a.address_id}>
                                        {addressText(a)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ) : null}

                    {selectedAddress ? (
                        <div style={styles.addressBlock}>
                            <p style={styles.addressLine}>
                                <strong>{t('addressLabel')}</strong> {addressText(selectedAddress)}
                            </p>
                            {rooms || bathrooms ? (
                                <p style={styles.addressLine}>
                                    <strong>{t('propertyTypeLabel')}</strong>{' '}
                                    {[rooms, bathrooms].filter(Boolean).join(', ')}
                                </p>
                            ) : null}
                        </div>
                    ) : null}

                    <button
                        type="button"
                        style={styles.howItWorksButton}
                        onClick={() => setShowHowItWorks(true)}
                    >
                        {t('howItWorksButton')}
                    </button>

                    <div style={styles.field}>
                        <label style={styles.label} htmlFor="package">
                            {t('choosePackageLabel')}
                        </label>
                        <select
                            id="package"
                            ref={(el) => { fieldRefs.current.package = el; }}
                            aria-invalid={invalidField === 'package' || undefined}
                            style={styles.select}
                            value={packageCode}
                            onChange={(e) => setPackageCode(e.target.value)}
                            required
                        >
                            <option value="">{t('choosePackagePlaceholder')}</option>
                            {packages.map((p) => (
                                <option key={p.recid} value={p.code}>{p.name}</option>
                            ))}
                        </select>
                        {selectedPackage ? (
                            <p style={styles.hint}>
                                {selectedPackage.description
                                    || t('cleaningsSummary', {
                                        paid: selectedPackage.cleanings_paid,
                                        bonus: selectedPackage.cleanings_bonus,
                                        total: selectedPackage.cleanings_included,
                                    })}
                            </p>
                        ) : null}
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label} htmlFor="frequency">
                            {t('frequencyLabel')}
                        </label>
                        <select
                            id="frequency"
                            ref={(el) => { fieldRefs.current.frequency = el; }}
                            aria-invalid={invalidField === 'frequency' || undefined}
                            style={styles.select}
                            value={cadence}
                            onChange={(e) => setCadence(e.target.value)}
                            disabled={cadenceEntries.length === 0}
                            required
                        >
                            <option value="">{t('frequencyPlaceholder')}</option>
                            {cadenceEntries.map(([key, multiplier]) => {
                                const percent = Math.round((1 - Number(multiplier)) * 100);
                                const label = CADENCE_LABEL_KEYS[key] ? t(CADENCE_LABEL_KEYS[key]) : key;
                                return (
                                    <option key={key} value={key}>
                                        {percent > 0 ? `${label} · ${t('frequencyDiscount', { percent })}` : label}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label} htmlFor="firstDate">
                            {t('firstDateLabel')}
                        </label>
                        <input
                            id="firstDate"
                            ref={(el) => { fieldRefs.current.firstDate = el; }}
                            aria-invalid={invalidField === 'firstDate' || undefined}
                            type="date"
                            style={styles.dateInput}
                            value={firstDate}
                            min={minDate}
                            onChange={(e) => {
                                // `min` is NOT a guard on iOS: some versions let
                                // the calendar hand back an earlier date and only
                                // mark the field invalid, so an owner picked
                                // today from a picker that showed today greyed.
                                // Reject it here, at the moment of the tap, and
                                // say why — the submit-time check alone let the
                                // customer fill the whole form before finding out.
                                const picked = e.target.value;
                                if (picked && picked < minDate) {
                                    setFormErrorKey('firstDateTooEarly');
                                    setInvalidField('firstDate');
                                    setFirstDate('');
                                    return;
                                }
                                setFormErrorKey('');
                                setInvalidField('');
                                setFirstDate(picked);
                            }}
                            required
                        />
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label} htmlFor="time">
                            {t('preferredTimeLabel')}
                        </label>
                        <select
                            id="time"
                            ref={(el) => { fieldRefs.current.time = el; }}
                            aria-invalid={invalidField === 'time' || undefined}
                            style={styles.select}
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                            required
                        >
                            <option value="">{t('preferredTimePlaceholder')}</option>
                            {availableSlots.map((slot) => (
                                <option key={slot} value={slot}>{slot}</option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label} htmlFor="freeService">
                            {t('freeServiceLabel')}
                        </label>
                        <select
                            id="freeService"
                            ref={(el) => { fieldRefs.current.freeService = el; }}
                            aria-invalid={invalidField === 'freeService' || undefined}
                            style={styles.select}
                            value={giftRecid}
                            onChange={(e) => setGiftRecid(e.target.value)}
                            required
                        >
                            <option value="">{t('freeServicePlaceholder')}</option>
                            {giftOptions.map((g) => (
                                <option key={g.recid} value={g.recid}>{giftLabel(g.name)}</option>
                            ))}
                        </select>
                    </div>

                    {addons.length > 0 ? (
                        <div style={styles.field} role="group" aria-labelledby="addonsGroupLabel">
                            <span id="addonsGroupLabel" style={styles.label}>{t('additionalOptionsLabel')}</span>
                            {offer.addons_ok === false ? (
                                <p style={styles.notice}>{t('addonsUnavailable')}</p>
                            ) : null}
                            {addons.map((a) => (
                                <label key={a.recid} style={styles.checkboxRow}>
                                    <input
                                        type="checkbox"
                                        style={styles.checkbox}
                                        checked={addonRecids.includes(a.recid)}
                                        onChange={() => toggleAddon(a.recid)}
                                    />
                                    <span style={styles.checkboxLabel}>
                                        {a.name} <span style={styles.addonPrice}>+{formatMoney(a.price)}</span>
                                    </span>
                                </label>
                            ))}
                            <p style={styles.hint}>{t('addonsPerCleaningNote')}</p>
                        </div>
                    ) : null}

                    {promotions.map((promo) => {
                        const eligible = eligiblePromotions.some((p) => p.recid === promo.recid);
                        const unlockedBy = packageNames(promo.applies_to_packages);
                        if (!eligible && !unlockedBy) return null;
                        // No page-side cap. packages_quote_POST §6 clamps ONLY when
                        // max_units > 0, so an absent cap genuinely means uncapped
                        // and inventing a number here would be this page deciding
                        // product policy. A ceiling belongs on the Airtable
                        // promotion row next to unit_price.
                        const rawMaxUnits = Number(promo.max_units);
                        const maxUnits = Number.isFinite(rawMaxUnits) && rawMaxUnits > 0
                            ? rawMaxUnits
                            : Infinity;
                        const units = Number(promoUnits[promo.code] || 0);
                        const variants = Array.isArray(promo.variants) ? promo.variants : [];
                        return (
                            <div key={promo.recid} style={styles.promoBlock}>
                                <h3 style={styles.promoTitle}>{promo.name}</h3>
                                {promo.description ? (
                                    <p style={styles.promoDesc}>{promo.description}</p>
                                ) : null}
                                {!eligible ? (
                                    <p style={styles.promoLocked}>
                                        {t('promoLockedNote', { packages: unlockedBy })}
                                    </p>
                                ) : null}
                                {eligible && promo.kind === 'per_unit' ? (
                                    <div style={styles.promoCounterRow}>
                                        <span style={styles.promoCounterLabel}>
                                            {t('promoQuantityLabel')} · {formatMoney(promo.unit_price)}
                                        </span>
                                        <div style={styles.promoCounter}>
                                            <button
                                                type="button"
                                                style={units === 0
                                                    ? { ...styles.promoCounterButton, ...styles.promoCounterButtonDisabled }
                                                    : styles.promoCounterButton}
                                                onClick={() => setPromoUnitCount(promo.code, Math.max(0, units - 1))}
                                                disabled={units === 0}
                                                aria-label={t('promoDecrease')}
                                            >
                                                −
                                            </button>
                                            <span style={styles.promoCounterValue} aria-live="polite">{units}</span>
                                            <button
                                                type="button"
                                                style={units >= maxUnits
                                                    ? { ...styles.promoCounterButton, ...styles.promoCounterButtonDisabled }
                                                    : styles.promoCounterButton}
                                                onClick={() => setPromoUnitCount(promo.code, Math.min(maxUnits, units + 1))}
                                                disabled={units >= maxUnits}
                                                aria-label={t('promoIncrease')}
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                ) : null}
                                {eligible && promo.kind === 'variant' ? (
                                    <select
                                        style={styles.select}
                                        value={promoVariants[promo.code] || ''}
                                        onChange={(e) => setPromoVariants((current) => ({
                                            ...current,
                                            [promo.code]: e.target.value,
                                        }))}
                                        aria-label={promo.name}
                                    >
                                        <option value="">{t('promoVariantNone')}</option>
                                        {variants.map((v) => (
                                            <option key={v.code} value={v.code}>
                                                {v.code} · {formatMoney(v.price)}
                                            </option>
                                        ))}
                                    </select>
                                ) : null}
                            </div>
                        );
                    })}

                    <div style={styles.field}>
                        <label style={styles.label} htmlFor="comments">
                            {t('commentsLabel')}
                        </label>
                        <textarea
                            id="comments"
                            style={styles.textarea}
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder={t('commentsPlaceholder')}
                            rows={4}
                        />
                    </div>

                    <div style={styles.field} role="group" aria-labelledby="paymentGroupLabel">
                        <span id="paymentGroupLabel" style={styles.label}>{t('selectPayment')}</span>
                        <label style={styles.checkboxRow}>
                            <input
                                type="radio"
                                name="method"
                                style={styles.checkbox}
                                checked={method === 'bank'}
                                onChange={() => setMethod('bank')}
                            />
                            <span style={styles.checkboxLabel}>{t('payViaBank')}</span>
                        </label>
                        <label style={styles.checkboxRow}>
                            <input
                                type="radio"
                                name="method"
                                style={styles.checkbox}
                                checked={method === 'card'}
                                onChange={() => setMethod('card')}
                            />
                            <span style={styles.checkboxLabel}>{t('payViaCard')}</span>
                        </label>
                    </div>

                    {/* aria-live: ticking an add-on or a promo counter changes the
                        total with no other feedback, which is silent for a
                        non-sighted user. */}
                    <div style={styles.summary} aria-live="polite">
                        <h2 style={styles.summaryTitle}>{t('priceBreakdownTitle')}</h2>
                        {quoting ? <p style={styles.summaryHint}>{t('quoteLoading')}</p> : null}
                        {!quoting && quoteError ? (
                            <p style={styles.errorText} role="alert">{t(quoteError.key || 'quoteFailed')}</p>
                        ) : null}
                        {!quoting && !quoteError && !quote ? (
                            <p style={styles.summaryHint}>{t('quoteIncomplete')}</p>
                        ) : null}
                        {!quoting && !quoteError && quote ? (
                            <>
                                {quoteLines.map((line, index) => (
                                    <div key={`${line.key}-${index}`} style={styles.summaryRow}>
                                        <span style={styles.summaryLabel}>{lineLabel(line)}</span>
                                        <span style={styles.summaryValue}>
                                            {line.key === 'cleanings'
                                                ? `× ${line.amount}`
                                                : formatMoney(line.amount)}
                                        </span>
                                    </div>
                                ))}
                                <div style={styles.summaryTotalRow}>
                                    <span style={styles.summaryTotalLabel}>{t('totalOnlyLabel')}</span>
                                    <span style={styles.summaryTotalValue}>{formatMoney(quote.total)}</span>
                                </div>
                                {quote.cleanings_included > 0 ? (
                                    <p style={styles.summaryHint}>
                                        {t('perCleaningNote', {
                                            price: formatMoney(quote.payment_per_cleaning),
                                            count: quote.cleanings_included,
                                        })}
                                    </p>
                                ) : null}
                            </>
                        ) : null}
                    </div>

                    {formErrorKey ? (
                        <p style={styles.errorText} role="alert">{t(formErrorKey)}</p>
                    ) : null}
                    {checkoutError ? (
                        <p style={styles.errorText} role="alert">{t(checkoutError.key || 'checkoutFailed')}</p>
                    ) : null}

                    <button
                        type="button"
                        style={canBuy ? styles.primaryButton : { ...styles.primaryButton, ...styles.buttonDisabled }}
                        onClick={handleBuy}
                        disabled={!canBuy}
                    >
                        {submitting ? t('checkoutStarting') : t('buyPackageButton')}
                    </button>
                </div>
            </main>

            {showHowItWorks && (
                <div
                    style={styles.modalOverlay}
                    onClick={() => setShowHowItWorks(false)}
                >
                    <div
                        ref={modalRef}
                        style={styles.modalCard}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="howItWorksTitle"
                        tabIndex={-1}
                    >
                        <button
                            type="button"
                            style={styles.modalClose}
                            onClick={() => setShowHowItWorks(false)}
                            aria-label={t('closeButton')}
                        >
                            ×
                        </button>
                        <h2 id="howItWorksTitle" style={styles.modalTitle}>
                            {t('howItWorksTitle')}
                        </h2>
                        <ul style={styles.modalList}>
                            <li style={styles.modalListItem}>{t('howItWorksFlexible')}</li>
                            <li style={styles.modalListItem}>{t('howItWorksCost')}</li>
                            <li style={styles.modalListItem}>{t('howItWorksPriority')}</li>
                            <li style={styles.modalListItem}>{t('howItWorksPause')}</li>
                            <li style={styles.modalListItem}>{t('howItWorksCustomizable')}</li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh',
        background: 'var(--wc-bg-secondary)',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'center',
    },
    logo: {
        height: '36px',
        width: 'auto',
        objectFit: 'contain',
    },
    main: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '8px 20px 40px',
    },
    errorMain: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
    },
    loadingText: {
        color: 'var(--wc-text-secondary)',
        fontSize: '15px',
        padding: '40px 20px',
    },
    card: {
        width: '100%',
        maxWidth: '520px',
        background: 'var(--wc-bg-primary)',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-card)',
        boxShadow: 'var(--wc-shadow-card)',
        padding: '28px 24px',
        boxSizing: 'border-box',
    },
    title: {
        fontSize: '22px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
        margin: '0 0 16px',
    },
    addressBlock: {
        background: 'var(--wc-bg-secondary)',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-input)',
        padding: '12px 14px',
        marginBottom: '16px',
    },
    addressLine: {
        margin: '4px 0',
        fontSize: '14px',
        color: 'var(--wc-text-secondary)',
        lineHeight: 1.45,
    },
    howItWorksButton: {
        display: 'block',
        width: '100%',
        background: 'transparent',
        color: 'var(--wc-brand-dark)',
        border: '1px solid var(--wc-brand)',
        borderRadius: 'var(--wc-radius-btn)',
        padding: '10px 14px',
        fontSize: '14px',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        marginBottom: '20px',
    },
    field: {
        marginBottom: '14px',
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: 600,
        color: 'var(--wc-text-primary)',
        marginBottom: '6px',
    },
    hint: {
        margin: '6px 0 0',
        fontSize: '13px',
        lineHeight: 1.45,
        color: 'var(--wc-text-tertiary)',
    },
    notice: {
        margin: '0 0 8px',
        fontSize: '13px',
        lineHeight: 1.45,
        color: 'var(--wc-text-secondary)',
        background: 'var(--wc-bg-secondary)',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-input)',
        padding: '8px 10px',
    },
    // 16px, not 15: iOS Safari zooms the viewport when a control smaller than
    // 16px takes focus and does not zoom back out, leaving the rest of the form
    // clipped for the session. This page is opened mostly from phone email
    // clients, i.e. exactly where that fires. The fix is the font size, NOT
    // `maximum-scale=1` on the viewport, which suppresses the zoom by breaking
    // pinch-zoom (a WCAG 1.4.4 failure).
    select: {
        width: '100%',
        padding: '12px',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-input)',
        background: 'var(--wc-bg-primary)',
        color: 'var(--wc-text-primary)',
        fontSize: '16px',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    },
    input: {
        width: '100%',
        padding: '12px',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-input)',
        background: 'var(--wc-bg-primary)',
        color: 'var(--wc-text-primary)',
        fontSize: '16px',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    },
    // iOS Safari renders `input[type=date]` as a native control that ignores the
    // shared input box: the value sits centred and the control claims its own
    // intrinsic width, so the field came out wider than the selects above it and
    // read as a different kind of control. Dropping the native appearance puts it
    // back on the same box; the explicit height matches a padded select, which the
    // native control does not derive from padding alone.
    dateInput: {
        width: '100%',
        padding: '12px',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-input)',
        background: 'var(--wc-bg-primary)',
        color: 'var(--wc-text-primary)',
        fontSize: '16px',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        WebkitAppearance: 'none',
        appearance: 'none',
        textAlign: 'left',
        minHeight: '46px',
        display: 'block',
    },
    textarea: {
        width: '100%',
        padding: '12px',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-input)',
        background: 'var(--wc-bg-primary)',
        color: 'var(--wc-text-primary)',
        fontSize: '16px',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        resize: 'vertical',
        minHeight: '90px',
    },
    checkboxRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        margin: '8px 0',
        cursor: 'pointer',
    },
    checkbox: {
        width: '18px',
        height: '18px',
        accentColor: 'var(--wc-brand)',
        cursor: 'pointer',
        flexShrink: 0,
    },
    checkboxLabel: {
        fontSize: '14px',
        color: 'var(--wc-text-primary)',
    },
    addonPrice: {
        color: 'var(--wc-brand-dark)',
        fontWeight: 600,
    },
    summary: {
        marginTop: '20px',
        padding: '16px 14px',
        background: 'var(--wc-brand-subtle)',
        borderRadius: 'var(--wc-radius-card)',
    },
    summaryTitle: {
        margin: '0 0 10px',
        fontSize: '16px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '5px 0',
        fontSize: '14px',
    },
    summaryLabel: {
        color: 'var(--wc-text-secondary)',
    },
    summaryValue: {
        color: 'var(--wc-text-primary)',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    summaryTotalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '12px',
        marginTop: '10px',
        paddingTop: '10px',
        borderTop: '1px solid var(--wc-brand-light)',
    },
    summaryTotalLabel: {
        fontSize: '16px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
    },
    summaryTotalValue: {
        fontSize: '18px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
        whiteSpace: 'nowrap',
    },
    summaryHint: {
        margin: '8px 0 0',
        fontSize: '13px',
        lineHeight: 1.45,
        color: 'var(--wc-text-secondary)',
    },
    errorText: {
        margin: '10px 0 0',
        fontSize: '14px',
        lineHeight: 1.45,
        color: 'var(--wc-error)',
    },
    primaryButton: {
        display: 'block',
        width: '100%',
        background: 'var(--wc-brand)',
        color: 'var(--wc-on-brand)',
        border: 'none',
        borderRadius: 'var(--wc-radius-btn)',
        padding: '14px 16px',
        fontSize: '16px',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        boxShadow: 'var(--wc-shadow-cta)',
        marginTop: '16px',
    },
    buttonDisabled: {
        opacity: 0.55,
        cursor: 'default',
        boxShadow: 'none',
    },
    errorTitle: {
        fontSize: '20px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
        margin: '0 0 12px',
    },
    errorBody: {
        fontSize: '15px',
        color: 'var(--wc-text-secondary)',
        lineHeight: 1.5,
        margin: '0 0 16px',
    },
    supportLink: {
        color: 'var(--wc-brand-dark)',
        fontWeight: 600,
        textDecoration: 'none',
        fontSize: '15px',
    },
    modalOverlay: {
        position: 'fixed',
        inset: 0,
        background: 'var(--wc-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
    },
    modalCard: {
        position: 'relative',
        width: '100%',
        maxWidth: '480px',
        background: 'var(--wc-bg-primary)',
        borderRadius: 'var(--wc-radius-card)',
        padding: '24px 20px',
        boxShadow: 'var(--wc-shadow-card)',
        boxSizing: 'border-box',
        maxHeight: '85vh',
        overflowY: 'auto',
    },
    modalClose: {
        position: 'absolute',
        top: '8px',
        right: '12px',
        background: 'transparent',
        border: 'none',
        fontSize: '28px',
        lineHeight: 1,
        color: 'var(--wc-text-tertiary)',
        cursor: 'pointer',
        padding: '4px 8px',
    },
    modalTitle: {
        fontSize: '20px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
        margin: '0 24px 12px 0',
    },
    modalList: {
        margin: 0,
        paddingLeft: '20px',
        color: 'var(--wc-text-secondary)',
        fontSize: '14px',
        lineHeight: 1.5,
    },
    modalListItem: {
        margin: '8px 0',
    },
    promoBlock: {
        position: 'relative',
        marginBottom: '14px',
        padding: '16px 14px 14px',
        background: 'var(--wc-brand-subtle)',
        border: '1.5px dashed var(--wc-brand)',
        borderRadius: 'var(--wc-radius-card)',
    },
    promoTitle: {
        margin: '0 0 4px',
        fontSize: '16px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
    },
    promoDesc: {
        margin: '0 0 12px',
        fontSize: '13px',
        lineHeight: 1.45,
        color: 'var(--wc-text-secondary)',
    },
    promoLocked: {
        margin: '0',
        fontSize: '13px',
        lineHeight: 1.45,
        fontWeight: 600,
        color: 'var(--wc-brand-dark)',
    },
    promoCounterRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
    },
    promoCounterLabel: {
        fontSize: '14px',
        fontWeight: 600,
        color: 'var(--wc-text-primary)',
    },
    promoCounter: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    // 44x44: these are the controls that ADD MONEY, and at 32px with an 8px gap a
    // mis-tap on a phone lands on the neighbour and changes the charge.
    promoCounterButton: {
        width: '44px',
        height: '44px',
        background: 'var(--wc-bg-primary)',
        color: 'var(--wc-brand-dark)',
        border: '1px solid var(--wc-brand)',
        borderRadius: 'var(--wc-radius-btn)',
        fontSize: '18px',
        fontWeight: 700,
        fontFamily: 'inherit',
        cursor: 'pointer',
        lineHeight: 1,
        padding: 0,
    },
    // The explicit color/background above override the UA's default :disabled
    // greying, so without this a disabled counter button looks identical to a
    // live one and the customer taps something that does nothing.
    promoCounterButtonDisabled: {
        opacity: 0.4,
        cursor: 'default',
    },
    promoCounterValue: {
        minWidth: '32px',
        textAlign: 'center',
        fontSize: '16px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
    },
};

export default BuyPackage;
